import React from 'react';
import { useEntity } from '@backstage/plugin-catalog-react';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { InfoCard } from '@backstage/core-components';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';

import { useEnv } from './EnvContext';
import {
  CoalitionApp,
  allEnvs,
  findApp,
  urlsForApp,
} from '../coalitions';

const useStyles = makeStyles(theme => ({
  linkBtn: {
    textTransform: 'none',
    fontSize: 12,
    minWidth: 0,
    padding: '2px 8px',
  },
  linkGroup: { display: 'inline-flex', gap: 4 },
  currentEnv: {
    backgroundColor:
      theme.palette.type === 'dark' ? 'rgba(66, 165, 245, 0.12)' : '#eff6ff',
  },
  currentEnvCell: {
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    fontWeight: 600,
  },
  none: { color: theme.palette.text.disabled, fontSize: 12 },
  tierBanner: {
    backgroundColor: theme.palette.type === 'dark' ? '#1f2937' : '#f3f4f6',
    fontSize: 11,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    fontWeight: 600,
    color: theme.palette.text.secondary,
  },
  prodBanner: {
    backgroundColor: theme.palette.type === 'dark' ? '#1e3a8a' : '#ede9fe',
    color: theme.palette.type === 'dark' ? '#dbeafe' : '#5b21b6',
  },
}));

// One app × all envs across staging + production. Staging rows first,
// production rows after a tier divider. Current env highlighted.
export const EntitySparrowDeskDeploymentMatrix = () => {
  const classes = useStyles();
  const { entity } = useEntity();
  const config = useApi(configApiRef);
  const { coalition, env } = useEnv(entity, config);

  const app: CoalitionApp | undefined = coalition
    ? findApp(coalition, entity)
    : undefined;

  if (!coalition || !app || allEnvs(coalition).length === 0) return null;

  const linkBtn = (label: string, href: string) => (
    <Button
      size="small"
      variant="outlined"
      color="primary"
      className={classes.linkBtn}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
    >
      {label}
    </Button>
  );

  const renderRows = (envs: string[]) =>
    envs.map(e => {
      const urls = urlsForApp(app, e, coalition);
      if (!urls) return null;
      const isCurrent = e === env;
      return (
        <TableRow key={e} className={isCurrent ? classes.currentEnv : undefined}>
          <TableCell className={isCurrent ? classes.currentEnvCell : undefined}>
            {e}
          </TableCell>
          <TableCell align="center">
            {urls.config ? linkBtn('Config', urls.config) : <span className={classes.none}>—</span>}
          </TableCell>
          <TableCell align="center">
            {urls.vault ? linkBtn('Vault', urls.vault) : <span className={classes.none}>—</span>}
          </TableCell>
          <TableCell align="center">
            {urls.argocd ? linkBtn(urls.argocdAppName || 'ArgoCD', urls.argocd) : <span className={classes.none}>—</span>}
          </TableCell>
          <TableCell align="center">
            {urls.jenkins.length > 0 ? (
              <div className={classes.linkGroup}>
                {urls.jenkins.map(j => (
                  <React.Fragment key={j.pipeline}>
                    {linkBtn(shortPipelineLabel(j.pipeline), j.url)}
                  </React.Fragment>
                ))}
              </div>
            ) : (
              <span className={classes.none}>—</span>
            )}
          </TableCell>
          <TableCell align="center">
            {urls.logs ? linkBtn('Logs', urls.logs) : <span className={classes.none}>—</span>}
          </TableCell>
          <TableCell align="center">
            {urls.metrics ? linkBtn('Metrics', urls.metrics) : <span className={classes.none}>—</span>}
          </TableCell>
        </TableRow>
      );
    });

  const bannerRow = (label: string, count: number, isProd: boolean) => (
    <TableRow>
      <TableCell
        colSpan={7}
        className={`${classes.tierBanner} ${isProd ? classes.prodBanner : ''}`}
      >
        {label} · {count} {count === 1 ? 'env' : 'envs'}
      </TableCell>
    </TableRow>
  );

  const stagingEnvs = coalition.staging.envs;
  const productionEnvs = coalition.production.envs;
  const totalEnvs = stagingEnvs.length + productionEnvs.length;

  return (
    <InfoCard title={`${app.name} across ${totalEnvs} envs`}>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
        Each row deep-links to the tools scoped to that env. Currently
        selected env is highlighted.
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Env / namespace</TableCell>
              <TableCell align="center">Config</TableCell>
              <TableCell align="center">Vault</TableCell>
              <TableCell align="center">ArgoCD</TableCell>
              <TableCell align="center">Jenkins</TableCell>
              <TableCell align="center">Logs</TableCell>
              <TableCell align="center">Metrics</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stagingEnvs.length > 0 && bannerRow('Staging', stagingEnvs.length, false)}
            {renderRows(stagingEnvs)}
            {productionEnvs.length > 0 && bannerRow('Production', productionEnvs.length, true)}
            {renderRows(productionEnvs)}
          </TableBody>
        </Table>
      </TableContainer>
    </InfoCard>
  );
};

// Short label for the matrix column (BE, FE, WebsiteDeployment gets
// abbreviated by keeping just the first word).
function shortPipelineLabel(pipeline: string): string {
  if (pipeline === 'BackendDeployments') return 'BE';
  if (pipeline === 'FrontendDeployments') return 'FE';
  return pipeline;
}

export default EntitySparrowDeskDeploymentMatrix;
